import { Injectable } from '@angular/core';
import { FitList } from '@shared/fit-api-client.service';
import { ProgramTag } from './program-tag.service';
import { ProgramSearch, ProgramStageKey } from './types';

@Injectable({ providedIn: 'root' })
export class ProgramListEnrichmentService {
    public enrichListItem(item: ProgramSearch): ProgramSearch {
        const tags = this.resolveListTags(item);
        return {
            ...item,
            tags,
            tag_ids: item.tag_ids?.length
                ? item.tag_ids
                : tags.map(tag => tag.id).filter(id => id > 0),
            image_url: this.resolveImageUrl(item),
            is_from_library: item.is_from_library ?? false,
            is_free: item.is_free ?? false,
            is_coming_soon: item.is_coming_soon ?? false,
            stage: item.stage ?? this.deriveStage(item),
            workout_count: item.trainings_count ?? item.workout_count ?? item.workouts_count ?? null,
            environment_names: item.environment_names ?? item.location_names ?? '',
        };
    }

    public enrichList(response: FitList<ProgramSearch>): FitList<ProgramSearch> {
        const data = response.data.map(item => this.enrichListItem(item));
        return { ...response, data };
    }

    private deriveStage(item: ProgramSearch): ProgramStageKey {
        if (item.is_published) {
            return 'live';
        }
        if (item.is_coming_soon) {
            return 'soon';
        }
        return 'draft';
    }

    private resolveImageUrl(item: ProgramSearch): string {
        const image = item.image as ProgramSearch['image'] | string | undefined;
        if (typeof image === 'string' && image) {
            return image;
        }
        if (image && typeof image === 'object') {
            return image.thumbnail_url || image.url || item.image_url || '';
        }
        return item.image_url || '';
    }

    private resolveListTags(item: ProgramSearch): ProgramTag[] {
        if (item.tags?.length) {
            return item.tags.map(tag => ({
                id: tag.id,
                name: tag.name,
                is_custom_tag: tag.is_custom_tag ?? true,
            }));
        }
        if (item.tag_ids?.length && item.tag_names) {
            const names = this.parseNames(item.tag_names);
            return item.tag_ids.map((id, index) => ({
                id,
                name: names[index] ?? names[0] ?? '',
                is_custom_tag: true,
            }));
        }
        return this.parseNames(item.tag_names).map(name => ({
            id: 0,
            name,
            is_custom_tag: true,
        }));
    }

    private parseNames(value?: string): string[] {
        if (!value) {
            return [];
        }
        return value.split(',').map(s => s.trim()).filter(Boolean);
    }
}
